/**
 * Topo da lista de empresas: o CONTADOR (quantas, em que status, quantas
 * pedem atencao) e os TOTAIS do periodo com a comparacao e a projecao.
 *
 * Decisoes nao obvias:
 *  - Todo numero do contador e um BOTAO que recorta a lista abaixo: "3 em
 *    implantacao" que nao leva as tres e so decoracao. O foco vai para a
 *    lista depois do clique (quem chamou decide, via `onFiltrar`).
 *  - A barra segmentada usa largura proporcional mas o numero escrito vem
 *    junto: cor e largura sozinhas nao comunicam contagem.
 *  - Projecao e LINEAR e diz isso: "no ritmo do periodo, 30 dias rendem X".
 *    Nao e previsao, e o ritmo atual escrito em outra unidade.
 *  - O lojista ve retorno; a administracao ve retorno E a comissao geral
 *    (faturamento do Converte Facil). Nada aqui e "gasto".
 */

import type { ReactNode } from 'react';
import { Icon, Panel, type IconName } from '../../../components/ui';
import { VALOR_AUSENTE, formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import type { CompanyStatus } from '../../../types';
import { projetar } from '../comparar';
import type { CompaniesTotals, ConsultaLista, NivelSaude } from '../types';
import { NIVEL_SAUDE, STATUS_EMPRESA, STATUS_LISTA } from '../vocabulario';
import { Delta } from './Delta';

const BARRA: Readonly<Record<CompanyStatus, string>> = {
  ativa: 'bg-status-good',
  onboarding: 'bg-status-warning',
  pausada: 'bg-ink-subtle',
};

function BotaoContagem({
  valor,
  rotulo,
  ponto,
  onClick,
  titulo,
}: {
  readonly valor: number;
  readonly rotulo: string;
  readonly ponto: string;
  readonly onClick: () => void;
  readonly titulo: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={titulo}
      disabled={valor === 0}
      className="group flex items-center gap-2 rounded-lg px-2 py-1 text-left text-sm transition-colors hover:bg-ink/5 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent"
    >
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${ponto}`} />
      <span className="font-semibold tabular-nums text-ink">{formatCount(valor)}</span>
      <span className="text-ink-muted group-hover:text-ink">{rotulo}</span>
    </button>
  );
}

export function ContadorDeEmpresas({
  porStatus,
  porSaude,
  novas,
  total,
  onFiltrar,
  className = '',
}: {
  readonly porStatus: Readonly<Record<CompanyStatus, number>>;
  readonly porSaude: Readonly<Record<NivelSaude, number>>;
  readonly novas: number;
  readonly total: number;
  readonly onFiltrar: (patch: Partial<ConsultaLista>) => void;
  readonly className?: string;
}): JSX.Element {
  return (
    <Panel className={`flex flex-col gap-4 p-5 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium text-ink-muted">Empresas cadastradas</h2>
          <p className="mt-1 text-4xl font-semibold tracking-tight text-ink">{formatCount(total)}</p>
        </div>
        <span aria-hidden="true" className="grid h-10 w-10 place-items-center rounded-xl bg-brand/10 text-brand">
          <Icon name="empresa" size="md" />
        </span>
      </div>

      <div>
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-ink/10" role="img" aria-label={STATUS_LISTA.map((s) => `${formatCount(porStatus[s])} ${STATUS_EMPRESA[s].plural.toLowerCase()}`).join(', ')}>
          {STATUS_LISTA.map((s) => (total === 0 || porStatus[s] === 0 ? null : <span key={s} className={`h-full ${BARRA[s]}`} style={{ width: `${(porStatus[s] * 100) / total}%` }} />))}
        </div>
        <div className="mt-2 flex flex-wrap gap-x-1 gap-y-1">
          {STATUS_LISTA.map((s) => (
            <BotaoContagem key={s} valor={porStatus[s]} rotulo={STATUS_EMPRESA[s].plural.toLowerCase()} ponto={BARRA[s]} onClick={() => onFiltrar({ status: s })} titulo={`Mostrar só as empresas com status ${STATUS_EMPRESA[s].label}`} />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-1 gap-y-1 border-t border-line pt-3">
        <BotaoContagem
          valor={porSaude.critica}
          rotulo="críticas"
          ponto="bg-status-critical"
          onClick={() => onFiltrar({ saude: 'critica' })}
          titulo={NIVEL_SAUDE.critica.explica}
        />
        <BotaoContagem valor={porSaude.atencao} rotulo="em atenção" ponto="bg-status-warning" onClick={() => onFiltrar({ saude: 'atencao' })} titulo={NIVEL_SAUDE.atencao.explica} />
        <BotaoContagem
          valor={novas}
          rotulo="novas no período"
          ponto="bg-info"
          onClick={() => onFiltrar({ ordem: { coluna: 'criada', desc: true } })}
          titulo="Cadastradas dentro da janela do cabeçalho. Clique para ordenar pelo cadastro mais recente."
        />
      </div>
    </Panel>
  );
}

function Celula({ icon, rotulo, titulo, valor, delta, apoio }: { readonly icon: IconName; readonly rotulo: string; readonly titulo: string; readonly valor: string; readonly delta: ReactNode; readonly apoio?: string }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-xl border border-line bg-elevated px-4 py-3" title={titulo}>
      <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
        <Icon name={icon} size="xs" />
        {rotulo}
      </span>
      <span className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">{valor}</span>
      {delta}
      {apoio === undefined ? null : <span className="text-xs text-ink-subtle">{apoio}</span>}
    </div>
  );
}

export function TotaisDoPeriodo({
  totals,
  anterior,
  dias,
  periodo,
  className = '',
}: {
  readonly totals: CompaniesTotals;
  readonly anterior: CompaniesTotals | null;
  /** Dias cobertos pela janela, para o ritmo. */
  readonly dias: number;
  readonly periodo: string;
  readonly className?: string;
}): JSX.Element {
  const projecao = projetar(totals.commissionCents, dias, 30);
  const projecaoRecuperado = projetar(totals.recoveredAmountCents, dias, 30);
  return (
    <Panel className={`flex flex-col gap-4 p-5 ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-medium text-ink-muted">Retorno de todas as empresas</h2>
          <p className="text-xs text-ink-subtle">{periodo} · comparado com o período anterior de mesmo tamanho</p>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Celula
          icon="moeda"
          rotulo="Recuperado geral"
          titulo="Valor pago pelos leads que a régua trouxe de volta, somando todas as empresas. É a base da comissão."
          valor={formatBRLFromCents(totals.recoveredAmountCents)}
          delta={<Delta atual={totals.recoveredAmountCents} anterior={anterior === null ? null : anterior.recoveredAmountCents} tipo="dinheiro" />}
        />
        <Celula
          icon="comissao"
          rotulo="Comissão geral"
          titulo="O que o Converte Fácil recebe sobre o recuperado, pelo percentual de cada empresa: o faturamento da plataforma."
          valor={formatBRLFromCents(totals.commissionCents)}
          delta={<Delta atual={totals.commissionCents} anterior={anterior === null ? null : anterior.commissionCents} tipo="dinheiro" />}
        />
        <Celula
          icon="empresa"
          rotulo="Líquido das empresas"
          titulo="Recuperado menos a comissão: o que ficou com as empresas e que teria sido perdido."
          valor={formatBRLFromCents(totals.netAmountCents)}
          delta={<Delta atual={totals.netAmountCents} anterior={anterior === null ? null : anterior.netAmountCents} tipo="dinheiro" />}
        />
        <Celula
          icon="carrinho"
          rotulo="Vendas recuperadas"
          titulo="Pagamentos creditados à régua: até 7 dias depois de um contato do motor, ao último contato."
          valor={formatCount(totals.recoveredCount)}
          delta={<Delta atual={totals.recoveredCount} anterior={anterior === null ? null : anterior.recoveredCount} tipo="contagem" />}
          apoio={`Taxa geral ${formatRatio(totals.recoveredCount, totals.abandonedCount)} dos abandonos`}
        />
      </div>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-dashed border-line px-3 py-2 text-xs text-ink-muted">
        <Icon name="grafico" size="xs" className="text-info" />
        <span>
          No ritmo deste período, 30 dias rendem{' '}
          <strong className="text-ink">{projecao === null ? VALOR_AUSENTE : formatBRLFromCents(projecao)}</strong> de comissão sobre{' '}
          <strong className="text-ink">{projecaoRecuperado === null ? VALOR_AUSENTE : formatBRLFromCents(projecaoRecuperado)}</strong> recuperados. É o ritmo atual em outra unidade, não uma previsão.
        </span>
      </p>
    </Panel>
  );
}
